import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, useIsHost, useMyPlayer } from '../stores/gameStore';
import { useGameActions, useSocket } from '../hooks/useSocket';

const VETO_WAITING_MESSAGES = [
  'Watching the skulls pile up...',
  'The bartender is counting the grievances...',
  'Someone is being very picky...',
  'The veto hammer is being polished...',
  'Judging the judges...',
  'Ensuring the quiz stays high-proof...',
  'Waiting for the final executioner...'
];

const VotingView = () => {
  const { gameState, clientState } = useGameStore();
  const isHost = useIsHost();
  const myPlayer = useMyPlayer();
  const { voteCategory, removeVote, confirmVotes, startGenerating, kickPlayer } = useGameActions();
  const { leaveRoom } = useSocket();

  const [localVotes, setLocalVotes] = useState<Record<string, number>>({});
  const [msgIndex, setMsgIndex] = useState(0);
  const [confirmKickId, setConfirmKickId] = useState<string | null>(null);

  // Also check if status is still VOTING - prevents flash of voting UI during transition
  const myConfirmed = myPlayer?.hasVoted || gameState.status !== 'VOTING';
  const allDecided = gameState.players.every(p => p.hasVoted);

  const totalVotesUsed = Object.values(localVotes).reduce((a, b) => a + b, 0);
  const skullsPerPlayer = gameState.config.votesPerPlayer;
  const skullsRemaining = skullsPerPlayer - totalVotesUsed;

  const waitingFor = gameState.players.filter(p => !p.hasVoted);
  const readyCount = gameState.players.length - waitingFor.length;

  const sortedByDownvotes = [...gameState.categories].sort((a, b) => b.downvotes - a.downvotes);
  const removalLimit = gameState.config.vetoRemovalCount;
  const dangerIds =
    allDecided && sortedByDownvotes.length > removalLimit
      ? sortedByDownvotes.slice(0, removalLimit).map(c => c.id)
      : [];

  // Rotate waiting messages
  useEffect(() => {
    if (myConfirmed && !allDecided) {
      const interval = setInterval(() => {
        setMsgIndex(prev => (prev + 1) % VETO_WAITING_MESSAGES.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [myConfirmed, allDecided]);

  const handleVote = (id: string) => {
    if (skullsRemaining > 0 && !myConfirmed) {
      setLocalVotes(prev => ({
        ...prev,
        [id]: (prev[id] || 0) + 1
      }));
      voteCategory(id);
      if (navigator.vibrate) navigator.vibrate(15);
    }
  };

  const handleReset = () => {
    if (myConfirmed) return;
    Object.entries(localVotes).forEach(([catId, count]) => {
      for (let i = 0; i < count; i++) {
        removeVote(catId);
      }
    });
    setLocalVotes({});
    if (navigator.vibrate) navigator.vibrate(40);
  };

  const handleConfirm = () => {
    confirmVotes();
    if (navigator.vibrate) navigator.vibrate(60);
  };

  const handleKickClick = (id: string) => {
    if (confirmKickId === id) {
      kickPlayer(id);
      setConfirmKickId(null);
    } else {
      setConfirmKickId(id);
      setTimeout(() => setConfirmKickId(null), 3000);
    }
  };

  // Waiting screen after confirming votes
  if (myConfirmed && !allDecided) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-2xl flex flex-col items-center space-y-12 pb-24"
      >
        {/* Leave Room Button */}
        <button
          onClick={leaveRoom}
          className="self-start text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
        >
          LEAVE ROOM
        </button>

        <div className="relative">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute -inset-16 bg-red-600/10 blur-[60px] rounded-full"
          />
          <div className="relative flex gap-8 text-7xl">
            <motion.span animate={{ y: [0, -10, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}>💀</motion.span>
            <motion.span animate={{ y: [0, -15, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}>💀</motion.span>
            <motion.span animate={{ y: [0, -10, 0] }} transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}>💀</motion.span>
          </div>
        </div>

        <div className="text-center space-y-4">
          <h2 className="text-6xl font-black italic tracking-tighter uppercase">
            VETO <span className="text-red-600">LOBBY</span>
          </h2>
          <div className="h-8">
            <AnimatePresence mode="wait">
              <motion.p
                key={msgIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-xl font-bold text-gray-400 italic"
              >
                {VETO_WAITING_MESSAGES[msgIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        <div className="w-full space-y-6">
          <div className="flex justify-between items-end px-2">
            <span className="text-xs font-black uppercase tracking-[0.3em] text-red-500">Skull Count Progress</span>
            <span className="text-2xl font-black">{readyCount} / {gameState.players.length} READY</span>
          </div>
          <div className="w-full h-4 bg-white/5 rounded-full p-1 border border-white/10 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(readyCount / gameState.players.length) * 100}%` }}
              className="h-full bg-gradient-to-r from-red-800 to-red-500 rounded-full shadow-[0_0_20px_rgba(220,38,38,0.5)]"
            />
          </div>
        </div>

        <div className="w-full glass p-8 rounded-[2rem] border-b-8 border-white/10">
          <h3 className="text-center text-[10px] font-black uppercase tracking-[0.5em] text-white/30 mb-8">Veto Status</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {gameState.players.map(p => {
              const isConfirming = confirmKickId === p.id;
              const isMe = p.id === clientState.myId;
              return (
                <motion.div
                  key={p.id}
                  layout
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all relative ${
                    p.hasVoted
                      ? 'bg-red-500/10 border border-red-500/30'
                      : 'bg-white/5 border border-white/5'
                  } ${isConfirming ? 'border-red-600 bg-red-600/10' : ''}`}
                >
                  {isHost && !isMe && (
                    <button
                      type="button"
                      onClick={() => handleKickClick(p.id)}
                      className={`absolute -top-1 -right-1 h-5 px-1 rounded-full flex items-center justify-center font-black text-[7px] shadow-lg z-10 transition-all ${
                        isConfirming ? 'bg-red-600 text-white w-auto scale-110' : 'bg-red-600 text-white w-5 hover:scale-110'
                      }`}
                    >
                      {isConfirming ? 'KICK?' : '✕'}
                    </button>
                  )}
                  <div className="relative">
                    <span className={`text-4xl transition-all ${p.hasVoted ? 'grayscale-0' : 'grayscale brightness-50'}`}>
                      {p.avatar}
                    </span>
                    {p.hasVoted && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold"
                      >
                        ✓
                      </motion.div>
                    )}
                  </div>
                  <span className={`text-[10px] font-black uppercase truncate w-full text-center ${p.hasVoted ? 'text-red-500' : 'text-white/40'}`}>
                    {p.name}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }

  // Voting interface
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="w-full max-w-md space-y-4 pb-24"
    >
      {/* Leave Room Button */}
      <button
        onClick={leaveRoom}
        className="text-[10px] font-black uppercase tracking-widest text-white/30 hover:text-red-500 transition-colors"
      >
        LEAVE ROOM
      </button>

      <div className="text-center space-y-2">
        <h2 className="text-4xl font-black uppercase italic tracking-tighter">
          {allDecided ? 'THE ' : 'PLACE YOUR '}
          <span className="text-red-500">{allDecided ? 'VERDICT' : 'VETO'}</span>
        </h2>

        <AnimatePresence>
          {gameState.nicheCategory && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              className="bg-neon-purple/20 border-2 border-neon-purple/40 p-4 rounded-2xl mb-4"
            >
              <div className="flex flex-col items-center">
                <p className="text-[10px] font-black uppercase tracking-[0.1em] text-neon-purple/80">
                  AI PRE-REJECTED
                </p>
                {gameState.nicheCommentary && (
                  <p className="text-lg font-black italic text-white leading-tight mt-1">
                    "{gameState.nicheCommentary}"
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!allDecided && (
          <div className="space-y-2">
            <p className="text-gray-400 font-bold text-lg">
              Tap to place <span className="text-white bg-red-600 px-2 py-0.5 rounded mx-1">{skullsRemaining}</span> skulls
            </p>
          </div>
        )}
      </div>

      {/* Category Grid */}
      <div className="grid grid-cols-2 gap-3">
        {gameState.categories.map(cat => {
          const mySkulls = localVotes[cat.id] || 0;
          const isDanger = dangerIds.includes(cat.id);
          return (
            <button
              key={cat.id}
              onClick={() => handleVote(cat.id)}
              disabled={skullsRemaining === 0 || myConfirmed || allDecided}
              className={`relative h-28 glass rounded-2xl p-3 flex flex-col items-center justify-center transition-all border-b-4 ${
                isDanger ? 'border-red-600 bg-red-950/20 shadow-[0_0_20px_rgba(220,38,38,0.3)]' : 'border-white/10'
              } ${mySkulls > 0 ? 'scale-[1.05] border-red-500' : ''}`}
            >
              <span className={`text-sm font-black text-center uppercase leading-tight line-clamp-2 ${isDanger ? 'text-red-500 line-through opacity-60' : ''}`}>
                {cat.text}
              </span>

              {allDecided && (
                <div className="absolute top-1 right-1 flex items-center gap-0.5 bg-black/60 px-1.5 py-0.5 rounded-full z-10">
                  <span className="text-xs">💀</span>
                  <span className="font-black text-[10px]">{cat.downvotes}</span>
                </div>
              )}

              {!allDecided && (
                <div className="flex flex-wrap gap-0.5 mt-2 h-4 items-center justify-center">
                  {Array.from({ length: mySkulls }).map((_, i) => (
                    <span key={i} className="text-xs">💀</span>
                  ))}
                </div>
              )}

              {isDanger && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -bottom-2 bg-red-600 text-white text-[8px] font-black px-2 py-0.5 rounded-full"
                >
                  PURGED
                </motion.div>
              )}
            </button>
          );
        })}
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-black/80 backdrop-blur-lg flex gap-3 z-50 border-t border-white/5">
        {!myConfirmed ? (
          <>
            <button
              onClick={handleReset}
              className="flex-1 py-4 rounded-xl font-black text-xs uppercase bg-white/10 text-white active:scale-95"
            >
              Reset
            </button>
            <button
              onClick={handleConfirm}
              className="flex-[2] py-4 rounded-xl font-black text-lg uppercase bg-red-600 text-white active:scale-95 shadow-lg shadow-red-600/20"
            >
              LOCK IN
            </button>
          </>
        ) : allDecided && isHost ? (
          <button
            onClick={startGenerating}
            className="w-full bg-neon-purple py-4 rounded-xl font-black text-xl active:scale-95 shadow-[0_0_30px_rgba(217,70,239,0.3)]"
          >
            GENERATE QUIZ
          </button>
        ) : (
          <div className="w-full text-center py-2">
            <div className="text-neon-blue font-black text-lg italic animate-pulse">
              HOST IS COOKING THE RESULTS...
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default VotingView;
